import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

/**
 * Trigger types that can start an automation rule.
 */
export type TriggerType = 'status_change' | 'task_completion' | 'date_trigger' | 'condition_met';

/**
 * Condition types for evaluating whether a rule should fire.
 */
export type ConditionType = 'if_then' | 'branch' | 'always';

/**
 * Action types that a rule can perform when triggered.
 */
export type ActionType = 'assign' | 'status_change' | 'notify' | 'create_task';

/**
 * Operators available for condition expressions.
 */
export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'greater_than'
  | 'less_than'
  | 'in'
  | 'not_in';

/** Direction of a date-trigger offset relative to the watched date. */
export type DateOffsetDirection = 'before' | 'on' | 'after';

/**
 * Trigger configuration shape.
 */
export interface TriggerConfig {
  entityType?: string;
  field?: string;
  fromValue?: string;
  toValue?: string;
  schedule?: string;
  /** date_trigger: which date column to watch (e.g. due_date). */
  dateField?: string;
  /** date_trigger: how many days before/after the date to fire. */
  offsetDays?: number;
  /** date_trigger: whether the offset is before/on/after the date. */
  offsetDirection?: DateOffsetDirection;
}

/**
 * Condition configuration shape.
 */
export interface ConditionClause {
  field?: string;
  operator?: ConditionOperator;
  value?: string | string[];
}

export interface ConditionConfig {
  field?: string;
  operator?: ConditionOperator;
  value?: string | string[];
  conditions?: ConditionClause[];
  logic?: 'and' | 'or';
  branches?: Array<{
    condition: { field: string; operator: ConditionOperator; value: string | string[] };
    action: ActionConfig;
  }>;
}

/**
 * Action configuration shape.
 */
export interface ActionConfig {
  target?: string;
  value?: string;
  assigneeRule?: string;
  templateId?: string;
  notificationTemplate?: string;
  recipients?: string[];
  // notify action (matches backend NotifyActionConfig)
  channel?: 'email' | 'in_app' | 'both';
  recipient_type?: 'user' | 'client' | 'role';
  recipient_id?: string;
  template?: string;
  title?: string;
  body?: string;
  // assign / status_change / create_task (backend snake_case config)
  target_field?: string;
  is_mandatory?: boolean;
  description?: string;
}

/**
 * Automation rule entity matching the backend data model.
 */
export interface AutomationRule {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  triggerType: TriggerType;
  triggerConfig: TriggerConfig;
  conditionType: ConditionType | null;
  conditionConfig: ConditionConfig | null;
  actionType: ActionType;
  actionConfig: ActionConfig;
  workflowTemplateId: string | null;
  isActive: boolean;
  executionCount: number;
  lastExecutedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Execution log entry for a rule.
 */
export interface ExecutionLogEntry {
  id: string;
  tenantId: string;
  ruleId: string;
  triggerEntityType: string;
  triggerEntityId: string;
  actionTaken: string;
  actionResult: Record<string, unknown> | null;
  executedAt: string;
}

/**
 * Dry run (test) result.
 */
export interface DryRunResult {
  wouldTrigger: boolean;
  matchedEntities: number;
  simulatedActions: Array<{
    entityId: string;
    entityType: string;
    action: string;
    details: Record<string, unknown>;
  }>;
  errors: string[];
}

/**
 * Create/update rule request payload.
 */
export interface AutomationRuleRequest {
  name: string;
  description?: string;
  triggerType: TriggerType;
  triggerConfig: TriggerConfig;
  conditionType?: ConditionType;
  conditionConfig?: ConditionConfig;
  actionType: ActionType;
  actionConfig: ActionConfig;
  workflowTemplateId?: string;
  isActive?: boolean;
}

// Query keys
const automationKeys = {
  all: ['automation-rules'] as const,
  lists: () => [...automationKeys.all, 'list'] as const,
  list: (params?: Record<string, string>) => [...automationKeys.lists(), params] as const,
  details: () => [...automationKeys.all, 'detail'] as const,
  detail: (id: string) => [...automationKeys.details(), id] as const,
  log: (ruleId: string) => [...automationKeys.all, 'log', ruleId] as const,
};

/**
 * The automation API returns rows in snake_case. The frontend (types,
 * designer, active toggle) works in camelCase, so normalize here. Without this
 * mapping `rule.isActive` is always undefined — breaking the active indicator
 * and the toggle.
 */
function mapRule(raw: Record<string, unknown>): AutomationRule {
  const get = <T>(camel: string, snake: string): T =>
    (raw[camel] !== undefined ? raw[camel] : raw[snake]) as T;
  return {
    id: get('id', 'id'),
    tenantId: get('tenantId', 'tenant_id'),
    name: get('name', 'name'),
    description: get('description', 'description') ?? null,
    triggerType: get('triggerType', 'trigger_type'),
    triggerConfig: (get('triggerConfig', 'trigger_config') as TriggerConfig) ?? {},
    conditionType: get('conditionType', 'condition_type') ?? null,
    conditionConfig: (get('conditionConfig', 'condition_config') as ConditionConfig) ?? null,
    actionType: get('actionType', 'action_type'),
    actionConfig: (get('actionConfig', 'action_config') as ActionConfig) ?? {},
    workflowTemplateId: get('workflowTemplateId', 'workflow_template_id') ?? null,
    isActive: Boolean(get('isActive', 'is_active')),
    executionCount: Number(get('executionCount', 'execution_count') ?? 0),
    lastExecutedAt: get('lastExecutedAt', 'last_executed_at') ?? null,
    createdAt: get('createdAt', 'created_at'),
    updatedAt: get('updatedAt', 'updated_at'),
  };
}

/**
 * Hook to fetch all automation rules.
 * Validates: Requirements 12.31
 */
export function useAutomationRules() {
  return useQuery({
    queryKey: automationKeys.lists(),
    queryFn: async () => {
      const response =
        await apiClient.get<ApiResponse<Record<string, unknown>[]>>('/automation/rules');
      return (response.data.data ?? []).map(mapRule);
    },
  });
}

/** A selectable field for the automation rule designer (entity-scoped). */
export interface AutomationField {
  value: string;
  label: string;
}

/** A date column that can drive a date_trigger, for a given entity. */
export interface AutomationEntityDateField {
  value: string;
  label: string;
  kind: 'deadline' | 'expiry';
}

/** A resolvable notification/assignment recipient for an entity. */
export interface AutomationEntityRecipient {
  /** e.g. 'rule:client', 'rule:manager'. */
  value: string;
  label: string;
}

/** An automation-capable entity, as described by the backend registry. */
export interface AutomationEntity {
  value: string;
  label: string;
  dateFields: AutomationEntityDateField[];
  recipients: AutomationEntityRecipient[];
  hasStatus: boolean;
}

/** Option with a description, used for trigger/action pickers. */
export interface AutomationOption {
  value: string;
  label: string;
  description?: string;
}

/** Full metadata payload driving the rule designer's guided pickers. */
export interface AutomationMetadata {
  entities: AutomationEntity[];
  triggerTypes: AutomationOption[];
  actionTypes: AutomationOption[];
  operators: AutomationOption[];
  dateDirections: AutomationOption[];
}

/**
 * Hook to fetch the automation metadata (entities, trigger/action types,
 * operators, date directions) that powers the guided rule designer. Sourced
 * from the shared backend entity registry so the UI stays in sync with the
 * engine's capabilities automatically.
 */
export function useAutomationMetadata() {
  return useQuery({
    queryKey: [...automationKeys.all, 'metadata'] as const,
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<AutomationMetadata>>('/automation/metadata');
      return response.data.data;
    },
  });
}

/**
 * Hook to fetch the fields available for a given entity type, used to populate
 * the "Field" dropdown in the automation rule designer. Sourced from the shared
 * field_library (same catalogue as the form builder / data rules) plus common
 * entity columns. Disabled until an entity type is selected.
 */
export function useAutomationFields(entityType: string | undefined) {
  return useQuery({
    queryKey: [...automationKeys.all, 'fields', entityType ?? ''] as const,
    enabled: !!entityType,
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<AutomationField[]>>('/automation/fields', {
        params: { entity_type: entityType },
      });
      return response.data.data ?? [];
    },
  });
}

/**
 * Hook to fetch a single automation rule by ID.
 */
export function useAutomationRule(ruleId: string) {
  return useQuery({
    queryKey: automationKeys.detail(ruleId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>>>(
        `/automation/rules/${ruleId}`,
      );
      return mapRule(response.data.data);
    },
    enabled: !!ruleId,
  });
}

/**
 * Hook to fetch execution log for a specific rule.
 * Validates: Requirements 12.32
 */
function mapLog(raw: Record<string, unknown>): ExecutionLogEntry {
  const get = <T>(camel: string, snake: string): T =>
    (raw[camel] !== undefined ? raw[camel] : raw[snake]) as T;
  let actionResult = get<unknown>('actionResult', 'action_result');
  if (typeof actionResult === 'string') {
    try {
      actionResult = JSON.parse(actionResult);
    } catch {
      actionResult = null;
    }
  }
  return {
    id: get('id', 'id'),
    tenantId: get('tenantId', 'tenant_id'),
    ruleId: get('ruleId', 'rule_id'),
    triggerEntityType: get('triggerEntityType', 'trigger_entity_type') ?? '',
    triggerEntityId: get('triggerEntityId', 'trigger_entity_id') ?? '',
    actionTaken: get('actionTaken', 'action_taken') ?? '',
    actionResult: (actionResult as Record<string, unknown> | null) ?? null,
    executedAt: get('executedAt', 'executed_at') ?? '',
  };
}

/**
 * Hook to fetch execution log for a specific rule.
 * Validates: Requirements 12.32
 */
export function useAutomationRuleLog(ruleId: string) {
  return useQuery({
    queryKey: automationKeys.log(ruleId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(
        `/automation/rules/${ruleId}/log`,
      );
      return (response.data.data ?? []).map(mapLog);
    },
    enabled: !!ruleId,
  });
}

/**
 * Hook to create a new automation rule.
 */
export function useCreateAutomationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AutomationRuleRequest) => {
      const payload = {
        name: data.name,
        description: data.description,
        trigger_type: data.triggerType,
        trigger_config: data.triggerConfig,
        condition_type: data.conditionType,
        condition_config: data.conditionConfig,
        action_type: data.actionType,
        action_config: data.actionConfig,
        workflow_template_id: data.workflowTemplateId,
        is_active: data.isActive ?? true,
      };
      const response = await apiClient.post<ApiResponse<AutomationRule>>(
        '/automation/rules',
        payload,
      );
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationKeys.lists() });
    },
  });
}

/**
 * Hook to update an existing automation rule.
 */
export function useUpdateAutomationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...data }: AutomationRuleRequest & { id: string }) => {
      const payload = {
        name: data.name,
        description: data.description,
        trigger_type: data.triggerType,
        trigger_config: data.triggerConfig,
        condition_type: data.conditionType,
        condition_config: data.conditionConfig,
        action_type: data.actionType,
        action_config: data.actionConfig,
        workflow_template_id: data.workflowTemplateId,
        is_active: data.isActive,
      };
      const response = await apiClient.put<ApiResponse<AutomationRule>>(
        `/automation/rules/${id}`,
        payload,
      );
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: automationKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: automationKeys.lists() });
    },
  });
}

/**
 * Hook to toggle rule active/inactive status.
 */
export function useToggleAutomationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const response = await apiClient.patch<ApiResponse<AutomationRule>>(
        `/automation/rules/${id}`,
        { is_active: isActive },
      );
      return response.data.data;
    },
    // Optimistically flip only the targeted row so the correct toggle updates
    // immediately (and the on/off label with it), with rollback on error.
    onMutate: async ({ id, isActive }) => {
      await queryClient.cancelQueries({ queryKey: automationKeys.lists() });
      const previous = queryClient.getQueryData<AutomationRule[]>(automationKeys.lists());
      if (previous) {
        queryClient.setQueryData<AutomationRule[]>(
          automationKeys.lists(),
          previous.map((r) => (r.id === id ? { ...r, isActive } : r)),
        );
      }
      return { previous };
    },
    onError: (_err, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(automationKeys.lists(), context.previous);
      }
    },
    onSettled: (_data, _err, variables) => {
      queryClient.invalidateQueries({ queryKey: automationKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: automationKeys.lists() });
    },
  });
}

/**
 * Hook to delete an automation rule.
 */
export function useDeleteAutomationRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/automation/rules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: automationKeys.lists() });
    },
  });
}

/**
 * Hook to run a dry-run test of an automation rule.
 * Validates: Requirements 12.32
 */
export function useTestAutomationRule() {
  return useMutation({
    mutationFn: async ({ ruleId, entityType }: { ruleId: string; entityType?: string }) => {
      const response = await apiClient.post<ApiResponse<DryRunResult & Record<string, unknown>>>(
        `/automation/rules/${ruleId}/test`,
        entityType ? { trigger_entity_type: entityType } : {},
      );
      const data = response.data.data ?? {};
      return {
        wouldTrigger: Boolean(data.wouldTrigger ?? data.would_execute),
        matchedEntities: Number(data.matchedEntities ?? 0),
        simulatedActions: (data.simulatedActions as DryRunResult['simulatedActions']) ?? [],
        errors: (data.errors as string[]) ?? [],
      };
    },
  });
}
